import { useState, useEffect, useCallback, useRef } from "react";
import {
  Box,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  CircularProgress,
  useMediaQuery,
  useTheme,
  IconButton,
  Divider,
  Badge,
} from "@mui/material";
import {
  ExpandMore,
  People,
  Leaderboard,
  Close,
  Circle,
} from "@mui/icons-material";
import io from "socket.io-client";
import { getGuestDisplayName } from "../../../utils/guestDisplayUtils";

const RIGHT_SIDEBAR_WIDTH = 280;

const RightSidebar = ({ open, onToggle }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const [activeUsers, setActiveUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);

  const [activeSession, setActiveSession] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  const [loadingLeaderboard, setLoadingLeaderboard] = useState(false);

  const socketRef = useRef(null);

  const fetchActiveUsers = useCallback(async () => {
    try {
      setLoadingUsers(true);
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/api/active-users`
      );
      if (res.ok) {
        const data = await res.json();
        setActiveUsers(data.sessions || data || []);
      }
    } catch (err) {
      console.error("Error fetching active users:", err);
    } finally {
      setLoadingUsers(false);
    }
  }, []);

  const fetchActiveQuizSession = useCallback(async () => {
    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/api/live-quiz/flash/sessions`
      );
      if (!res.ok) return;
      const data = await res.json();
      const sessions = data.sessions || [];
      const active = sessions.find((s) => s.status === "active");
      setActiveSession(active || null);
      if (active) {
        fetchLeaderboard(active.session_id);
      } else {
        setLeaderboard([]);
      }
    } catch (err) {
      console.error("Error fetching active quiz session:", err);
    }
  }, []);

  const fetchLeaderboard = useCallback(async (sessionId) => {
    try {
      setLoadingLeaderboard(true);
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/api/live-quiz/sessions/${sessionId}/participants`
      );
      if (!res.ok) return;
      const data = await res.json();
      if (data.success && Array.isArray(data.participants)) {
        const sorted = data.participants
          .filter((p) => p && typeof p === "object")
          .sort((a, b) => (b.current_score || 0) - (a.current_score || 0));
        setLeaderboard(sorted);
      }
    } catch (err) {
      console.error("Error fetching leaderboard:", err);
    } finally {
      setLoadingLeaderboard(false);
    }
  }, []);

  useEffect(() => {
    fetchActiveUsers();
    fetchActiveQuizSession();

    const userInterval = setInterval(fetchActiveUsers, 10000);
    const quizInterval = setInterval(fetchActiveQuizSession, 15000);

    return () => {
      clearInterval(userInterval);
      clearInterval(quizInterval);
    };
  }, [fetchActiveUsers, fetchActiveQuizSession]);

  useEffect(() => {
    const baseUrl = process.env.REACT_APP_API_URL;
    const socket = io(baseUrl, { transports: ["websocket"] });
    socketRef.current = socket;

    socket.on("connect", () => {
      socket.emit("admin:join-active-users");
      socket.emit("flash:join-global");
    });

    socket.on("active-user:update", (payload) => {
      if (payload?.sessions) {
        setActiveUsers(payload.sessions);
      }
    });

    socket.on("flash:leaderboard", (payload) => {
      if (payload?.session_id && activeSession?.session_id === payload.session_id) {
        fetchLeaderboard(payload.session_id);
      }
    });

    socket.on("flash:activated", () => {
      fetchActiveQuizSession();
    });

    socket.on("flash:session_ended", () => {
      fetchActiveQuizSession();
    });

    return () => {
      if (socketRef.current) {
        socketRef.current.emit("admin:leave-active-users");
        socketRef.current.emit("flash:leave-global");
        socketRef.current.disconnect();
        socketRef.current = null;
      }
    };
  }, [activeSession?.session_id, fetchActiveQuizSession, fetchLeaderboard]);

  useEffect(() => {
    if (isMobile && open) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isMobile, open]);

  const groupedByUrl = {};
  activeUsers.forEach((u) => {
    const url = u.current_url || "/";
    if (!groupedByUrl[url]) groupedByUrl[url] = [];
    groupedByUrl[url].push(u);
  });

  const getUserDisplayName = (user) => {
    if (user.user_type === "google" || user.user_id) {
      const name = (user.username || "").trim();
      return name || "Registered User";
    }
    return getGuestDisplayName(user);
  };

  const getUserEmail = (user) => {
    if (user.user_type === "google" && user.username) {
      if (user.username.includes("@")) return user.username;
    }
    return null;
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "active":
        return "#4CAF50";
      case "idle":
        return "#FFC107";
      case "away":
        return "#F44336";
      default:
        return "#9E9E9E";
    }
  };

  const getLeaderboardName = (participant) => {
    const guest = (participant.guest_name || "").trim();
    if (guest) return guest;
    const fname = (participant.fname || "").trim();
    const lname = (participant.lname || "").trim();
    const combined = `${fname} ${lname}`.trim();
    return combined || "Player";
  };

  const getRankDisplay = (index) => {
    if (index === 0) return "🥇";
    if (index === 1) return "🥈";
    if (index === 2) return "🥉";
    return `#${index + 1}`;
  };

  return (
    <>
      {isMobile && open && (
        <Box
          onClick={onToggle}
          sx={{
            position: "fixed",
            inset: 0,
            backgroundColor: "rgba(0,0,0,0.6)",
            zIndex: 1199,
          }}
        />
      )}

      <Box
        sx={{
          position: isMobile ? "fixed" : "relative",
          top: 0,
          right: 0,
          width: open ? RIGHT_SIDEBAR_WIDTH : 0,
          minWidth: open ? RIGHT_SIDEBAR_WIDTH : 0,
          height: isMobile ? "100vh" : "100%",
          backgroundColor: "#0A0A0A",
          borderLeft: open ? "1px solid rgba(255,255,255,0.06)" : "none",
          transition:
            "width 0.3s cubic-bezier(0.4, 0, 0.2, 1), min-width 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
          overflow: "hidden",
          zIndex: isMobile ? 1200 : 1,
          display: "flex",
          flexDirection: "column",
        }}
      >
        {isMobile && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-start",
              px: 1.5,
              pt: 1.5,
              flexShrink: 0,
            }}
          >
            <IconButton
              size="small"
              onClick={onToggle}
              sx={{ color: "rgba(255,255,255,0.4)" }}
            >
              <Close fontSize="small" />
            </IconButton>
          </Box>
        )}

        <Box
          sx={{
            flex: 1,
            overflow: "auto",
            py: 1,
            "&::-webkit-scrollbar": { width: 4 },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: "rgba(255,255,255,0.1)",
              borderRadius: 2,
            },
          }}
        >
          {/* Users Accordion */}
          <Accordion
            defaultExpanded
            disableGutters
            sx={{
              backgroundColor: "transparent",
              boxShadow: "none",
              "&:before": { display: "none" },
              "&.Mui-expanded": { margin: 0 },
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMore sx={{ color: "rgba(255,255,255,0.3)", fontSize: 18 }} />}
              sx={{
                minHeight: 40,
                px: 2,
                "&.Mui-expanded": { minHeight: 40 },
                "& .MuiAccordionSummary-content": { my: 0.5 },
                "&:hover": { backgroundColor: "rgba(255,255,255,0.03)" },
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <People sx={{ fontSize: 18, color: "#1DB954" }} />
                <Typography
                  sx={{
                    fontSize: "0.85rem",
                    fontWeight: 700,
                    color: "#1DB954",
                    letterSpacing: -0.3,
                  }}
                >
                  Users
                </Typography>
                <Badge
                  badgeContent={activeUsers.length}
                  color="success"
                  max={999}
                  sx={{
                    ml: 0.5,
                    "& .MuiBadge-badge": {
                      fontSize: "0.55rem",
                      height: 16,
                      minWidth: 16,
                      backgroundColor: "#1DB954",
                    },
                  }}
                >
                  <Box sx={{ width: 8 }} />
                </Badge>
              </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ px: 1.5, pt: 0, pb: 1 }}>
              {loadingUsers && activeUsers.length === 0 ? (
                <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
                  <CircularProgress size={20} sx={{ color: "#1DB954" }} />
                </Box>
              ) : activeUsers.length === 0 ? (
                <Typography
                  sx={{ fontSize: "0.75rem", color: "rgba(255,255,255,0.3)", textAlign: "center", py: 2 }}
                >
                  No active users
                </Typography>
              ) : (
                Object.entries(groupedByUrl).map(([url, users]) => (
                  <Box key={url} sx={{ mb: 1.5 }}>
                    <Typography
                      sx={{
                        fontSize: "0.65rem",
                        fontWeight: 600,
                        color: "rgba(255,255,255,0.35)",
                        textTransform: "uppercase",
                        letterSpacing: 0.5,
                        px: 0.5,
                        mb: 0.5,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {url}
                    </Typography>
                    {users.map((user) => (
                      <Box
                        key={user.session_id}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          py: 0.5,
                          px: 0.5,
                          borderRadius: 1,
                          "&:hover": {
                            backgroundColor: "rgba(255,255,255,0.03)",
                          },
                        }}
                      >
                        <Circle
                          sx={{
                            fontSize: 8,
                            color: getStatusColor(user.status),
                            flexShrink: 0,
                          }}
                        />
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography
                            sx={{
                              fontSize: "0.75rem",
                              fontWeight: 500,
                              color: "rgba(255,255,255,0.8)",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {getUserDisplayName(user)}
                          </Typography>
                          {getUserEmail(user) ? (
                            <Typography
                              sx={{
                                fontSize: "0.6rem",
                                color: "rgba(255,255,255,0.35)",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {getUserEmail(user)}
                            </Typography>
                          ) : user.user_type === "guest" && user.user_id ? (
                            <Typography
                              sx={{
                                fontSize: "0.6rem",
                                color: "rgba(255,255,255,0.35)",
                              }}
                            >
                              ID: {user.user_id}
                            </Typography>
                          ) : null}
                        </Box>
                        <Chip
                          label={user.user_id ? "G" : "guest"}
                          size="small"
                          sx={{
                            height: 16,
                            fontSize: "0.55rem",
                            fontWeight: 600,
                            backgroundColor: user.user_id
                              ? "rgba(66,133,244,0.15)"
                              : "rgba(255,255,255,0.06)",
                            color: user.user_id
                              ? "#4285F4"
                              : "rgba(255,255,255,0.35)",
                            border: "none",
                            "& .MuiChip-label": { px: 0.5 },
                          }}
                        />
                      </Box>
                    ))}
                  </Box>
                ))
              )}
            </AccordionDetails>
          </Accordion>

          <Divider sx={{ borderColor: "rgba(255,255,255,0.06)", mx: 1.5 }} />

          {/* Leaderboard Accordion */}
          <Accordion
            defaultExpanded
            disableGutters
            sx={{
              backgroundColor: "transparent",
              boxShadow: "none",
              "&:before": { display: "none" },
              "&.Mui-expanded": { margin: 0 },
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMore sx={{ color: "rgba(255,255,255,0.3)", fontSize: 18 }} />}
              sx={{
                minHeight: 40,
                px: 2,
                "&.Mui-expanded": { minHeight: 40 },
                "& .MuiAccordionSummary-content": { my: 0.5 },
                "&:hover": { backgroundColor: "rgba(255,255,255,0.03)" },
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Leaderboard sx={{ fontSize: 18, color: "#FF9800" }} />
                <Typography
                  sx={{
                    fontSize: "0.85rem",
                    fontWeight: 700,
                    color: "#FF9800",
                    letterSpacing: -0.3,
                  }}
                >
                  Leaderboard
                </Typography>
                {activeSession && (
                  <Chip
                    label="LIVE"
                    size="small"
                    sx={{
                      height: 16,
                      fontSize: "0.55rem",
                      fontWeight: 700,
                      backgroundColor: "rgba(244,67,54,0.15)",
                      color: "#F44336",
                      border: "none",
                      animation: "pulse 2s infinite",
                      "@keyframes pulse": {
                        "0%": { opacity: 1 },
                        "50%": { opacity: 0.5 },
                        "100%": { opacity: 1 },
                      },
                      "& .MuiChip-label": { px: 0.5 },
                    }}
                  />
                )}
              </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ px: 1.5, pt: 0, pb: 1 }}>
              {!activeSession ? (
                <Typography
                  sx={{ fontSize: "0.75rem", color: "rgba(255,255,255,0.3)", textAlign: "center", py: 2 }}
                >
                  No active quiz session
                </Typography>
              ) : loadingLeaderboard && leaderboard.length === 0 ? (
                <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
                  <CircularProgress size={20} sx={{ color: "#FF9800" }} />
                </Box>
              ) : (
                <Box>
                  <Typography
                    sx={{
                      fontSize: "0.65rem",
                      fontWeight: 600,
                      color: "rgba(255,255,255,0.35)",
                      textTransform: "uppercase",
                      letterSpacing: 0.5,
                      px: 0.5,
                      mb: 0.5,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {activeSession.quiz_name}
                  </Typography>

                  {leaderboard.length === 0 ? (
                    <Typography
                      sx={{
                        fontSize: "0.75rem",
                        color: "rgba(255,255,255,0.3)",
                        textAlign: "center",
                        py: 1,
                      }}
                    >
                      No participants yet
                    </Typography>
                  ) : (
                    leaderboard.map((participant, index) => (
                      <Box
                        key={participant.participant_id || index}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          py: 0.5,
                          px: 0.5,
                          borderRadius: 1,
                          backgroundColor:
                            index < 3
                              ? `rgba(255, 215, 0, ${0.04 * (3 - index)})`
                              : "transparent",
                          "&:hover": {
                            backgroundColor:
                              index < 3
                                ? `rgba(255, 215, 0, ${0.06 * (3 - index)})`
                                : "rgba(255,255,255,0.03)",
                          },
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: "0.7rem",
                            fontWeight: 700,
                            color: index < 3 ? "#FFD700" : "rgba(255,255,255,0.5)",
                            minWidth: 24,
                            textAlign: "center",
                          }}
                        >
                          {getRankDisplay(index)}
                        </Typography>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography
                            sx={{
                              fontSize: "0.75rem",
                              fontWeight: index < 3 ? 600 : 400,
                              color: "rgba(255,255,255,0.8)",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {getLeaderboardName(participant)}
                          </Typography>
                          {participant.user_id && (participant.guest_name || "").trim() && (
                            <Typography
                              sx={{
                                fontSize: "0.6rem",
                                color: "rgba(255,255,255,0.35)",
                              }}
                            >
                              ID: {participant.user_id}
                            </Typography>
                          )}
                        </Box>
                        <Chip
                          label={participant.user_id ? "Reg" : "Guest"}
                          size="small"
                          sx={{
                            height: 16,
                            fontSize: "0.5rem",
                            fontWeight: 600,
                            backgroundColor: participant.user_id
                              ? "rgba(76,175,80,0.12)"
                              : "rgba(255,152,0,0.12)",
                            color: participant.user_id ? "#4CAF50" : "#FF9800",
                            border: "none",
                            "& .MuiChip-label": { px: 0.5 },
                          }}
                        />
                        <Typography
                          sx={{
                            fontSize: "0.7rem",
                            fontWeight: 700,
                            color: "#4CAF50",
                            minWidth: 30,
                            textAlign: "right",
                          }}
                        >
                          {participant.current_score || 0}
                        </Typography>
                      </Box>
                    ))
                  )}
                </Box>
              )}
            </AccordionDetails>
          </Accordion>
        </Box>
      </Box>
    </>
  );
};

export default RightSidebar;
